import { useState, createContext, useContext, useMemo } from 'react';
import { SUCURSALES } from '../utils/sucursalConfig';
import formatPrice from '../utils/formatPrice';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  // item shape: { id, nameEs, nameEn, opcionEs, opcionEn, adicionalesEs, adicionalesEn, price, quantity }
  const addToCart = (item) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const updateQuantity = (id, qty) => {
    if (qty <= 0) removeFromCart(id);
    else setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
  };

  const clearCart = () => setItems([]);

  const totalItems = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const totalPrice = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);

  const sendWhatsApp = (sucursal, lang = 'es') => {
    const config = SUCURSALES[sucursal];
    if (!config || items.length === 0) return;
    const lines = items.map(item => {
      const name = lang === 'en' ? (item.nameEn || item.nameEs) : item.nameEs;
      const opcion = lang === 'en' ? item.opcionEn : item.opcionEs;
      const adicionales = lang === 'en' ? item.adicionalesEn : item.adicionalesEs;
      const detail = [opcion ? `(${opcion})` : null, adicionales ? `+ ${adicionales}` : null]
        .filter(Boolean).join(' ');
      const subtotal = formatPrice(item.price * item.quantity);
      return `- ${name}${detail ? ` ${detail}` : ''} x${item.quantity} — ${subtotal}`;
    });
    const total = formatPrice(totalPrice);
    const body = `Hola! Quiero hacer un pedido 🍕\n\n${lines.join('\n')}\n\nTotal: ${total}`;
    window.open(`https://wa.me/${config.whatsapp}?text=${encodeURIComponent(body)}`, '_blank');
  };

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, sendWhatsApp }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
